import { useQuery } from '@apollo/client';
import { Box, Link, Paper, Typography } from '@mui/material';
import { GetAccountOrganizationDetails } from 'Apollo';
import { LocalContext } from 'Context/Local.context';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { useParams } from 'react-router-dom';
import { DataRow, PaperTitle } from 'Theme/components';
import { userFullName } from 'Tools';
import { OrganizationInviteNotifyModal } from './OrganizationInviteNotifyModal';

function OrganizationInvite() {
  const ts = useTranslate();
  const { onError } = useNotification();
  const { accountCode } = useParams<IUriParams>();
  const { availableAccounts, currentAccount } = useContext(LocalContext);

  /**
   * Outside the `/all` route, the URL segment is the account code. On `/all`, this block is only
   * displayed when a single account is administered (`PageDashboard` / `displayOrganizationInvite`),
   * so the only available account is used.
   */
  const effectiveAccountCode = useMemo(() => {
    if (accountCode !== 'all') {
      return accountCode;
    }
    return availableAccounts[0]?.code ?? '';
  }, [accountCode, availableAccounts]);

  const [open, setOpen] = useState(false);
  const handledError = useRef<Error | null>(null);
  const contextOrganization =
    currentAccount?.organization ??
    (accountCode === 'all' ? availableAccounts[0]?.organization : null);
  const { data, error } = useQuery<
    GetAccountOrganizationDetailsData,
    GetAccountOrganizationDetailsVars
  >(GetAccountOrganizationDetails, {
    variables: { accountCode: effectiveAccountCode },
    skip: !effectiveAccountCode || Boolean(contextOrganization),
    fetchPolicy: 'cache-and-network'
  });
  useEffect(() => {
    if (!error || handledError.current === error) {
      return;
    }

    handledError.current = error;
    onError(error);
  }, [error, onError]);

  const organization = contextOrganization ?? data?.account.organization;
  const ownerName =
    organization?.owners
      ?.map(owner => userFullName(owner.firstName, owner.lastName))
      .filter(Boolean)
      .join(', ') || '-';

  return (
    <Paper>
      <PaperTitle
        variant="subtitle1"
        title={ts('page.dashBoard.organizationInvite.title')}
        info={ts('page.dashBoard.organizationInvite.info')}
      />
      {organization ? (
        <Box p={2}>
          <DataRow
            label={ts('page.dashBoard.organizationInvite.organizationName')}
            divider
          >
            <Typography>{organization.name}</Typography>
          </DataRow>
          <DataRow
            label={ts('page.dashBoard.organizationInvite.ownerName')}
            divider
          >
            <Typography>{ownerName}</Typography>
          </DataRow>
          <DataRow
            label={ts('page.dashBoard.organizationInvite.creationDate')}
            divider={false}
          >
            <Typography>
              {organization.dateCreation ? (
                <FormattedDate value={organization.dateCreation} />
              ) : (
                '-'
              )}
            </Typography>
          </DataRow>
        </Box>
      ) : (
        <Box p={2} display="flex" flexDirection="column" gap={1}>
          <Typography paragraph>
            {ts('page.dashBoard.organizationInvite.description1')}
          </Typography>
          <Typography paragraph>
            {ts('page.dashBoard.organizationInvite.description2')}
          </Typography>
          <Typography paragraph>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => {
                if (!effectiveAccountCode) {
                  return;
                }
                setOpen(true);
              }}
              sx={{ verticalAlign: 'baseline' }}
            >
              {ts('page.dashBoard.organizationInvite.description3')}
            </Link>{' '}
            {ts('page.dashBoard.organizationInvite.description4')}
          </Typography>
        </Box>
      )}

      <OrganizationInviteNotifyModal
        open={open}
        onClose={() => setOpen(false)}
        accountCode={effectiveAccountCode}
      />
    </Paper>
  );
}

export default OrganizationInvite;
